import {
  AffiliateRankingListData,
  AffiliateReportFilterOption,
  AffiliateReportFilters,
  AffiliateReportRow,
  AffiliateReportSortBy,
  AffiliateReportSummary,
  AffiliateReportTableData,
} from '@/@types/AffiliateReport';
import useCurrencyStore from '@/store/useCurrencyStore';
import { getDefaultDate } from '@/utils/dateHelpers';
import {
  buildRankingsFromApiResponse,
  buildSummaryFromApi,
} from '@/utils/affiliateReportFormatters';
import { ReportsService } from '@/services/reports.service';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  parseAsArrayOf,
  parseAsIsoDateTime,
  parseAsString,
  parseAsStringEnum,
  useQueryState,
} from 'nuqs';
import { useEffect, useMemo, useState } from 'react';
import { DateRange } from 'react-day-picker';

const PER_PAGE = 10;

const SORT_OPTIONS = ['sales', 'revenue', 'profit'] as const;

const emptyMeta = {
  last_page: 1,
  current_page: 1,
  per_page: PER_PAGE,
  total: 0,
};

const emptySummary: AffiliateReportSummary = {
  totalRevenue: '—',
  totalCommissions: '—',
};

function parseLabelValuePairs(values: string[] | null): AffiliateReportFilterOption[] {
  if (!values?.length) return [];
  return values.map((entry) => {
    const [label, value] = entry.includes(':::')
      ? entry.split(':::')
      : [entry, entry];
    return { label, value };
  });
}

function serializeLabelValuePairs(options: AffiliateReportFilterOption[]): string[] {
  return options.map((o) => `${o.label}:::${o.value}`);
}

export function useAffiliateReport(rankingLabels: Record<string, string>) {
  const currency = useCurrencyStore((s) => s.currency);

  const [search, setSearch] = useQueryState('q', parseAsString.withDefault(''));
  const [sortBy, setSortBy] = useQueryState(
    'sort',
    parseAsStringEnum([...SORT_OPTIONS]).withDefault('revenue'),
  );
  const [fromDate, setFromDate] = useQueryState(
    'from',
    parseAsIsoDateTime.withDefault(getDefaultDate()),
  );
  const [toDate, setToDate] = useQueryState(
    'to',
    parseAsIsoDateTime.withDefault(getDefaultDate()),
  );
  const [productsRaw, setProductsRaw] = useQueryState(
    'products',
    parseAsArrayOf(parseAsString),
  );
  const [affiliatesRaw, setAffiliatesRaw] = useQueryState(
    'affiliates',
    parseAsArrayOf(parseAsString),
  );
  const [offersRaw, setOffersRaw] = useQueryState(
    'offers',
    parseAsArrayOf(parseAsString),
  );
  const [checkoutsRaw, setCheckoutsRaw] = useQueryState(
    'checkouts',
    parseAsArrayOf(parseAsString),
  );
  const [templatesRaw, setTemplatesRaw] = useQueryState(
    'templates',
    parseAsArrayOf(parseAsString),
  );

  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    search,
    sortBy,
    fromDate,
    toDate,
    productsRaw,
    affiliatesRaw,
    offersRaw,
    checkoutsRaw,
    templatesRaw,
    currency,
  ]);

  const dateRange: DateRange | undefined = useMemo(
    () => ({ from: fromDate || undefined, to: toDate || undefined }),
    [fromDate, toDate],
  );

  const products = useMemo(() => parseLabelValuePairs(productsRaw), [productsRaw]);
  const affiliates = useMemo(() => parseLabelValuePairs(affiliatesRaw), [affiliatesRaw]);
  const offers = useMemo(() => parseLabelValuePairs(offersRaw), [offersRaw]);
  const checkouts = useMemo(() => parseLabelValuePairs(checkoutsRaw), [checkoutsRaw]);
  const templates = useMemo(() => parseLabelValuePairs(templatesRaw), [templatesRaw]);

  const filters: AffiliateReportFilters = useMemo(
    () => ({
      search: search ?? '',
      products,
      affiliates,
      offers,
      checkouts,
      templates,
      sortBy: (sortBy ?? 'revenue') as AffiliateReportSortBy,
      dateRange,
    }),
    [search, products, affiliates, offers, checkouts, templates, sortBy, dateRange],
  );

  const filterKey = [
    currency,
    fromDate?.toISOString(),
    toDate?.toISOString(),
    productsRaw,
    affiliatesRaw,
    offersRaw,
    checkoutsRaw,
    templatesRaw,
    search,
  ] as const;

  const baseParams = {
    startDate: fromDate?.toISOString() ?? new Date().toISOString(),
    endDate: toDate?.toISOString() ?? new Date().toISOString(),
    currency,
    q: search && search.length > 1 ? search : undefined,
    products: products.map((p) => p.value),
    affiliates: affiliates.map((a) => a.value),
    offers: offers.map((o) => o.value),
    checkouts: checkouts.map((c) => c.value),
    templates: templates.map((c) => c.value),
  };

  const {
    data: summaryData,
    isLoading: isLoadingSummary,
    isFetching: isFetchingSummary,
  } = useQuery({
    queryKey: ['affiliate-report', 'summary', ...filterKey],
    queryFn: () => ReportsService.getAffiliateReportSummary(baseParams),
    staleTime: 60_000,
  });

  const {
    data: rankingsData,
    isLoading: isLoadingRankings,
    isFetching: isFetchingRankings,
  } = useQuery({
    queryKey: ['affiliate-report', 'rankings', ...filterKey],
    queryFn: () => ReportsService.getAffiliateReportRankings(baseParams),
    staleTime: 60_000,
  });

  const {
    data: listData,
    isLoading: isLoadingTable,
    isFetching: isFetchingTable,
  } = useQuery({
    queryKey: ['affiliate-report', 'list', ...filterKey, currentPage, sortBy],
    queryFn: () =>
      ReportsService.getAffiliateReportList({
        ...baseParams,
        page: currentPage,
        perPage: PER_PAGE,
        sortBy: sortBy ?? 'revenue',
      }),
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });

  const rankings = useMemo<AffiliateRankingListData[]>(
    () => buildRankingsFromApiResponse(rankingsData?.rankings, rankingLabels),
    [rankingsData?.rankings, rankingLabels],
  );

  const summary = useMemo(
    () => (summaryData ? buildSummaryFromApi(summaryData) : emptySummary),
    [summaryData],
  );

  const tableData: AffiliateReportTableData = useMemo(
    () => ({
      data: listData?.data ?? [],
      meta: listData?.meta ?? emptyMeta,
      links: {},
    }),
    [listData],
  );

  const handleDateRangeChange = (range: DateRange | undefined) => {
    if (range?.from) setFromDate(range.from);
    if (range?.to) setToDate(range.to);
  };

  const setProducts = (options: AffiliateReportFilterOption[]) =>
    setProductsRaw(options.length ? serializeLabelValuePairs(options) : null);

  const setAffiliates = (options: AffiliateReportFilterOption[]) =>
    setAffiliatesRaw(options.length ? serializeLabelValuePairs(options) : null);

  const setOffers = (options: AffiliateReportFilterOption[]) =>
    setOffersRaw(options.length ? serializeLabelValuePairs(options) : null);

  const setCheckouts = (options: AffiliateReportFilterOption[]) =>
    setCheckoutsRaw(options.length ? serializeLabelValuePairs(options) : null);

  const setTemplates = (options: AffiliateReportFilterOption[]) =>
    setTemplatesRaw(options.length ? serializeLabelValuePairs(options) : null);

  const clearAllFilters = () => {
    setSearch('');
    setProductsRaw(null);
    setAffiliatesRaw(null);
    setOffersRaw(null);
    setCheckoutsRaw(null);
    setTemplatesRaw(null);
    setSortBy('revenue');
  };

  const hasActiveFiltersBeyondDates =
    !!search ||
    sortBy !== 'revenue' ||
    products.length > 0 ||
    affiliates.length > 0 ||
    offers.length > 0 ||
    checkouts.length > 0 ||
    templates.length > 0;

  return {
    filters,
    summary,
    rankings,
    tableData,
    filteredAffiliates: tableData.data,
    isLoadingSummary: isLoadingSummary || isFetchingSummary,
    isLoadingRankings: isLoadingRankings || isFetchingRankings,
    isLoadingTable: isLoadingTable || isFetchingTable,
    currentPage,
    setCurrentPage,
    search: search ?? '',
    setSearch,
    sortBy: (sortBy ?? 'revenue') as AffiliateReportSortBy,
    setSortBy: (value: AffiliateReportSortBy) => setSortBy(value),
    dateRange,
    handleDateRangeChange,
    products,
    setProducts,
    affiliates,
    setAffiliates,
    offers,
    setOffers,
    checkouts,
    setCheckouts,
    templates,
    setTemplates,
    clearAllFilters,
    hasActiveFiltersBeyondDates,
  };
}
